import React, { useEffect, useMemo, createContext, ReactNode } from 'react';
import { defaultTokens, ThemeTokens } from './defaultTokens';
import { useActiveCampaign, ActiveCampaign } from '../hooks/useActiveCampaign';
import { writeTtlCache } from '../lib/localTtlCache';
import { isPreviewEnabled } from '../lib/preview';
import {
  THEME_BOOTSTRAP_KEY,
  THEME_BOOTSTRAP_VERSION,
  ThemeDeclarations,
  ThemeSnapshot,
} from './themeBootstrap';

export const ThemeContext = createContext<ThemeTokens>(defaultTokens);

// [token key in ThemeTokens.colors, Strapi designTokens field name] — the two
// diverge only where the Strapi field carries a "Color"/legacy suffix
// (headingColor, connectorColor, ...). Adding a new PRIMITIVE color token is
// a single entry here; toThemeTokens() below derives the merge from this
// table instead of a hand-written line per field. Always JS-defaulted via
// `?? defaultTokens.colors[tokenKey]` — never null, never removed once set.
const COLOR_TOKEN_FIELDS: Array<[keyof ThemeTokens['colors'], string]> = [
  ['primary', 'primary'],
  ['secondary', 'secondary'],
  ['extend1', 'extend1'],
  ['extend2', 'extend2'],
  ['accent1', 'accent1'],
  ['accent2', 'accent2'],
  ['background', 'background'],
  ['text', 'text'],
  ['heading', 'headingColor'],
  // Writes Bootstrap's own --bs-secondary-color directly —
  // powers .text-muted sitewide, no per-component CSS needed.
  ['secondary-color', 'mutedText'],
  // Writes --bs-link-color (+ -rgb companion) — powers
  // plain anchors and variant="link" buttons sitewide.
  ['link-color', 'linkColor'],
];

// The 16 "detail" tokens
// Unlike COLOR_TOKEN_FIELDS above, these are NEVER JS-defaulted — toThemeTokens()
// passes Strapi's raw value straight through (null stays null), and the
// effective value comes from a CSS fallback chain at each consuming
// selector instead: `var(--bs-x, var(--bs-<primitive>))` for the 6 SAFE
// tokens that can alias a primitive with zero drift
// (quoteText, quoteAccent, ctaAccentBg, donationAmountAccent,
// calculatorScreenText, faqAccordionBg), `var(--bs-x, <literal>)` with
// today's JS-default literal for the other 10 (quoteSurface, cardText,
// connectorColor, ctaAccentText, calculatorScreenBg, calculatorScreenPlus,
// calculatorTickerLabel, faqAccordionText, inputSurfaceBg,
// inputSurfaceText). This lets an admin explicitly clear a detail token
// back to "inherit" without a stale inline value surviving a campaign
// switch — see the null -> removals branch in toCssDeclarations below.
const DETAIL_TOKEN_FIELDS: Array<[keyof ThemeTokens['colors'], string]> = [
  ['quote-accent', 'quoteAccent'],
  ['quote-surface', 'quoteSurface'],
  ['quote-text', 'quoteText'],
  ['connector', 'connectorColor'],
  ['card-text', 'cardText'],
  ['cta-accent-bg', 'ctaAccentBg'],
  ['cta-accent-text', 'ctaAccentText'],
  ['donation-amount-accent', 'donationAmountAccent'],
  ['calculator-screen-bg', 'calculatorScreenBg'],
  ['calculator-screen-text', 'calculatorScreenText'],
  ['calculator-screen-plus', 'calculatorScreenPlus'],
  ['calculator-ticker-label', 'calculatorTickerLabel'],
  ['faq-accordion-bg', 'faqAccordionBg'],
  ['faq-accordion-text', 'faqAccordionText'],
  ['input-surface-bg', 'inputSurfaceBg'],
  ['input-surface-text', 'inputSurfaceText'],
  // dedicated tokens replacing the
  // accent2/extend2/extend1 primitive bindings that leaked into light theme
  ['nav-active-bg', 'navActiveBg'],
  ['nav-active-border', 'navActiveBorder'],
  ['faq-accordion-body-bg', 'faqAccordionBodyBg'],
  ['calculator-border-color', 'calculatorBorderColor'],
];

export const toThemeTokens = (campaign: ActiveCampaign | undefined): ThemeTokens => {
  const t: any = campaign?.designTokens || {};
  const heading = t.fontHeading ?? t.heading ?? defaultTokens.fonts.heading;
  const body = t.fontBody ?? t.body ?? defaultTokens.fonts.body;
  const headingUrl =
    t.fontHeadingUrl ?? t.headingUrl ?? defaultTokens.fonts.headingUrl;
  const bodyUrl = t.fontBodyUrl ?? t.bodyUrl ?? defaultTokens.fonts.bodyUrl;

  const colors = COLOR_TOKEN_FIELDS.reduce((acc, [tokenKey, strapiField]) => {
    acc[tokenKey] = t[strapiField] ?? defaultTokens.colors[tokenKey];
    return acc;
  }, {} as ThemeTokens['colors']);

  // Detail tokens: raw passthrough, no JS default — null means "inherit via
  // the CSS fallback chain," not "use defaultTokens.colors[tokenKey]."
  DETAIL_TOKEN_FIELDS.forEach(([tokenKey, strapiField]) => {
    colors[tokenKey] = t[strapiField] ?? null;
  });

  // Per-theme brand media. No ThemeTokens-level default — the fallback chain
  // lives at the consumer (AppNavBar: theme -> navbar brand -> bundled asset).
  const logoUrl = t.logo?.url ?? null;
  const wordmarkUrl = t.wordmark?.url ?? null;

  // Identity only — branch on `isDark` below, never on this key. Strapi stores it
  // exactly as the admin typed it ("Dark", " dark "), so it is folded to one
  // canonical form for display and equality. The typeof guard is real: this
  // arrives off an untyped network payload.
  const activeThemeKey =
    typeof t.key === 'string' ? t.key.trim().toLowerCase() || null : null;

  // Rides along on the resolved theme object, not the campaign. Coerced because
  // a theme row predating the field returns null/undefined.
  const isDark = t.isDark === true;

  return {
    colors,
    fonts: {
      heading,
      body,
      headingUrl,
      bodyUrl,
    },
    logoUrl,
    wordmarkUrl,
    activeThemeKey,
    isDark,
  };
};

const hexToRgb = (hex: string | null) => {
  if (hex === null) return null;
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!m) return null;
  return {
    r: parseInt(m[1], 16),
    g: parseInt(m[2], 16),
    b: parseInt(m[3], 16),
  };
};

// Pure: resolved tokens -> the exact set of custom properties to set and to
// clear. Kept side-effect free so the same result can be persisted for the
// next page load's pre-paint replay, and so scripts/inline-theme.js can be
// diffed against it (src/theme/themeBootstrap.test.ts) — that build script
// duplicates this mapping because CJS on Node cannot require the TS source.
//
// vars/removals are allocated per call on purpose: a module-level accumulator
// would keep values the live DOM no longer has after an in-session theme
// switch, and then persist them.
export const toCssDeclarations = (tokens: ThemeTokens): ThemeDeclarations => {
  const vars: Record<string, string> = {};
  const removals: string[] = [];
  const { colors, fonts } = tokens;

  // A detail token can be null (Strapi field cleared / never set — "inherit
  // via the CSS fallback chain"). It is removed rather than skipped: inline
  // styles persist across renders, so a skip would leak a stale value on an
  // in-session campaign switch.
  Object.entries(colors).forEach(([key, value]) => {
    if (value === null) {
      removals.push(`--bs-${key}`);
      removals.push(`--bs-${key}-rgb`);
      return;
    }
    vars[`--bs-${key}`] = value;
    const rgb = hexToRgb(value);
    if (rgb) vars[`--bs-${key}-rgb`] = `${rgb.r}, ${rgb.g}, ${rgb.b}`;
  });

  // Bootstrap's canonical body vars, for components that read those directly.
  vars['--bs-body-bg'] = colors.background;
  vars['--bs-body-color'] = colors.text;
  // Bootstrap's link-hover default is a hardcoded blue, not derived from
  // --bs-link-color — without this line hover states stay blue.
  vars['--bs-link-hover-color'] = colors['link-color'];

  vars['--font-heading'] = fonts.heading;
  vars['--font-body'] = fonts.body;

  return { vars, removals };
};

const applyDeclarations = (
  root: HTMLElement,
  decls: ThemeDeclarations,
  isDark: boolean
) => {
  // Two attributes, deliberately. data-bs-theme engages Bootstrap's own dark
  // block — borders, subtle/emphasis colors, dropdowns, modals, form controls,
  // and color-scheme, which that block sets itself. data-theme-dark is the
  // project's own hook, because data-bs-theme is already load-bearing
  // elsewhere: AppNavBar scopes it to the navbar to get a light foreground on
  // a light campaign, so a project rule written `[data-bs-theme="dark"] .x`
  // would also match inside that subtree. Every token below is an inline
  // style and outranks both. Removed rather than skipped when light —
  // attributes persist across an in-session campaign/theme switch.
  root.toggleAttribute('data-theme-dark', isDark);
  if (isDark) root.setAttribute('data-bs-theme', 'dark');
  else root.removeAttribute('data-bs-theme');

  decls.removals.forEach((name) => root.style.removeProperty(name));
  Object.entries(decls.vars).forEach(([name, value]) =>
    root.style.setProperty(name, value)
  );
};

const loadFontStylesheet = (href: string | undefined) => {
  if (!href) return;
  const trimmed = href.trim();
  if (!/^https?:\/\//i.test(trimmed)) return;
  if (document.querySelector(`link[data-font-href="${trimmed}"]`)) return;
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = trimmed;
  link.setAttribute('data-font-href', trimmed);
  document.head.appendChild(link);
};

export const CampaignThemeProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const { data: activeCampaign } = useActiveCampaign();

  // Memoized on activeCampaign — without this, toThemeTokens() re-derives all
  // ~19 detail tokens plus logo/wordmark lookups on every render (not just
  // when the campaign data actually changes), which also breaks referential
  // equality on the useEffect below (rerunning every DOM-mutation pass) and
  // on ThemeContext.Provider's value (rerendering every consumer).
  const data: ThemeTokens = useMemo(
    () => toThemeTokens(activeCampaign) || defaultTokens,
    [activeCampaign]
  );
  /** inject CSS variables once we have tokens */
  useEffect(() => {
    // Runs unconditionally (including the first render, before the campaign
    // resolves) so Poppins/any campaign-supplied font starts downloading as
    // early as possible — loadFontStylesheet is idempotent (skips if the
    // stylesheet is already present), so calling it again once the real
    // campaign's fonts resolve is safe.
    [data.fonts.headingUrl, data.fonts.bodyUrl].forEach(loadFontStylesheet);

    // Pre-fetch, toThemeTokens() yields the light defaults, and writing those
    // as inline styles would paint over a build-time bake or a localStorage
    // bootstrap that already has the right theme. theme.css's :root carries
    // the light floor instead. Also makes StrictMode's double-invoke a
    // non-event before the campaign resolves.
    if (activeCampaign === undefined) return;

    const decls = toCssDeclarations(data);
    applyDeclarations(document.documentElement, decls, data.isDark);

    // Preview routes reads to ?status=draft, so persisting here would pin an
    // unpublished theme onto every normal load on this browser for the TTL.
    if (!isPreviewEnabled()) {
      writeTtlCache<ThemeSnapshot>(THEME_BOOTSTRAP_KEY, {
        v: THEME_BOOTSTRAP_VERSION,
        vars: decls.vars,
        isDark: data.isDark,
      });
    }
  }, [data, activeCampaign]);

  return <ThemeContext.Provider value={data}>{children}</ThemeContext.Provider>;
};
